import React, { useState } from 'react';
import { FileText, Eye, Download, CheckCircle, XCircle, Clock, AlertCircle, Upload } from 'lucide-react';
import Button from '../../../components/ui/Button';

const DocumentsTab = ({ documents, userRole }) => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getQualityColor = (quality) => {
    switch (quality) {
      case 'high':
        return 'text-green-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const handleApprove = (docId) => {
    console.log('Approving document:', docId);
    // Approval logic would go here
  };

  const handleReject = (doc) => {
    setSelectedDocument(doc);
    setShowRejectModal(true);
  };

  const submitRejection = () => {
    console.log('Rejecting document:', selectedDocument?.id, 'Reason:', rejectionReason);
    setShowRejectModal(false);
    setRejectionReason('');
    setSelectedDocument(null);
    // Rejection logic would go here
  };

  const handleRequestReplacement = (docId) => {
    console.log('Requesting replacement for document:', docId);
    // Request replacement logic would go here
  };

  const handleViewDocument = (doc) => {
    console.log('Viewing document:', doc?.fileUrl);
    window.open(doc?.fileUrl, '_blank');
  };

  const handleDownloadDocument = async (doc) => {
    if (!doc?.fileUrl) {
      console.error('No file URL available for download');
      return;
    }
    
    try {
      // Fetch the file as a blob
      const response = await fetch(doc.fileUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch file');
      }
      
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.name || 'document';
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading document:', error);
      // Fallback: open in new tab if download fails
      window.open(doc.fileUrl, '_blank');
    }
  };

  // Group documents by player
  const documentsByPlayer = documents?.reduce((acc, doc) => {
    if (!acc?.[doc?.playerId]) {
      acc[doc?.playerId] = {
        playerName: doc?.playerName,
        documents: []
      };
    }
    acc?.[doc?.playerId]?.documents?.push(doc);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Documents by Player */}
      {Object.entries(documentsByPlayer || {})?.map(([playerId, playerData]) => (
        <div key={playerId} className="bg-white rounded-lg border border-gray-200">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h4 className="text-lg font-semibold text-gray-900">{playerData?.playerName}</h4>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {playerData?.documents?.map((doc) => (
                <div
                  key={doc?.id}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {/* Document Thumbnail */}
                  <div className="relative h-48 bg-gray-100 flex items-center justify-center">
                    {doc?.thumbnail ? (
                      <img
                        src={doc?.thumbnail}
                        alt={`Document thumbnail for ${doc?.name}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to placeholder if image fails to load
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div 
                      className="absolute inset-0 flex items-center justify-center bg-gray-100" 
                      style={{ display: doc?.thumbnail ? 'none' : 'flex' }}
                    >
                      <FileText className="w-16 h-16 text-gray-400" />
                    </div>
                    <div className="absolute top-2 right-2 flex gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(doc?.status)}`}>
                        {doc?.status}
                      </span>
                    </div>
                  </div>

                  {/* Document Info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h5 className="text-sm font-semibold text-gray-900 mb-1 truncate">
                          {doc?.name}
                        </h5>
                        <p className="text-xs text-gray-600 mb-1">
                          Type: {doc?.type?.replace('_', ' ')?.toUpperCase()}
                        </p>
                        <p className="text-xs text-gray-600">
                          Size: {doc?.fileSize}
                        </p>
                      </div>
                      {getStatusIcon(doc?.status)}
                    </div>

                    {/* Quality Indicator */}
                    {doc?.quality && (
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-600">Quality:</span>
                          <span className={`font-medium ${getQualityColor(doc?.quality)}`}>
                            {doc?.quality?.toUpperCase()}
                          </span>
                        </div>
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              doc?.quality === 'high' ?'bg-green-500'
                                : doc?.quality === 'medium' ?'bg-yellow-500' :'bg-red-500'
                            }`}
                            style={{
                              width:
                                doc?.quality === 'high' ?'100%'
                                  : doc?.quality === 'medium' ?'60%' :'30%'
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Upload Date */}
                    <p className="text-xs text-gray-500 mb-3">
                      Uploaded: {new Date(doc?.uploadedDate)?.toLocaleDateString()}
                    </p>

                    {/* Review Info */}
                    {doc?.reviewedBy && (
                      <div className="text-xs text-gray-600 mb-3 p-2 bg-gray-50 rounded">
                        <p>Reviewed by: {doc?.reviewedBy}</p>
                        <p>On: {new Date(doc?.reviewedDate)?.toLocaleDateString()}</p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleViewDocument(doc)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-xs flex items-center justify-center gap-1"
                      >
                        <Eye className="w-3 h-3" />
                        View
                      </Button>
                      <Button
                        onClick={() => handleDownloadDocument(doc)}
                        className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded text-xs flex items-center justify-center gap-1"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </Button>
                    </div>

                    {/* Reviewer Actions */}
                    {(userRole === 'reviewer' || userRole === 'super_admin') && doc?.status === 'pending' && (
                      <div className="flex gap-2 mt-2">
                        <Button
                          onClick={() => handleApprove(doc?.id)}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-xs flex items-center justify-center gap-1"
                        >
                          <CheckCircle className="w-3 h-3" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleReject(doc)}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-xs flex items-center justify-center gap-1"
                        >
                          <XCircle className="w-3 h-3" />
                          Reject
                        </Button>
                      </div>
                    )}

                    {/* Request Replacement for Rejected Documents */}
                    {doc?.status === 'rejected' && (userRole === 'reviewer' || userRole === 'super_admin') && (
                      <Button
                        onClick={() => handleRequestReplacement(doc?.id)}
                        className="w-full mt-2 bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded text-xs flex items-center justify-center gap-1"
                      >
                        <Upload className="w-3 h-3" />
                        Request Replacement
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Reject Document</h3>
            </div>

            <p className="text-gray-600 mb-4">
              Please provide a reason for rejecting this document:
            </p>

            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e?.target?.value)}
              placeholder="Enter rejection reason..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              rows="4"
            />

            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                  setSelectedDocument(null);
                }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg"
              >
                Cancel
              </Button>
              <Button
                onClick={submitRejection}
                disabled={!rejectionReason?.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reject Document
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;